import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles'
import {
  neutral,
  brand,
  accent,
  semantic,
  spacingUnit,
  fontFamily,
  fontWeight,
  inputFontSize,
  radius,
  shadow,
  touchTarget,
} from './tokens'

/**
 * Tema MUI del Sistema ATS, construido sobre los tokens de `./tokens`.
 *
 * Criterios transversales aplicados aquí (para no repetirlos en cada `sx`):
 *  - Mobile-first: los overrides parten del móvil y crecen con breakpoints.
 *  - Áreas táctiles >= 44px en punteros gruesos (dedo), densidad normal en ratón.
 *  - Campos de formulario a 16px para evitar el zoom automático de iOS.
 *  - Foco siempre visible (`:focus-visible`) con contraste AA.
 */

/** Anillo de foco reutilizable. */
const focusRing = (color = brand[600]) => ({
  outline: `2px solid ${color}`,
  outlineOffset: 2,
})

/** Regla que solo aplica en dispositivos con puntero grueso (táctil). */
const COARSE_POINTER = '@media (pointer: coarse)'

let theme = createTheme({
  spacing: spacingUnit,

  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },

  shape: {
    borderRadius: radius.sm,
  },

  palette: {
    mode: 'light',
    primary: {
      main: brand[700],
      light: brand[500],
      // `lighter` = superficie tintada para paneles informativos (usado con texto oscuro)
      lighter: brand[50],
      dark: brand[800],
      contrastText: neutral[0],
    },
    secondary: {
      main: accent[700],
      light: accent[500],
      lighter: accent[50],
      dark: accent[800],
      contrastText: neutral[0],
    },
    success: { ...semantic.success, lighter: semantic.success.surface },
    warning: { ...semantic.warning, lighter: semantic.warning.surface },
    error: { ...semantic.error, lighter: semantic.error.surface },
    info: { ...semantic.info, lighter: semantic.info.surface },
    grey: neutral,
    text: {
      primary: neutral[900],
      secondary: neutral[600],
      disabled: neutral[400],
    },
    divider: neutral[200],
    background: {
      default: neutral[50],
      paper: neutral[0],
    },
    action: {
      hover: alpha(brand[700], 0.04),
      selected: alpha(brand[700], 0.08),
      focus: alpha(brand[700], 0.12),
    },
  },

  typography: {
    fontFamily: fontFamily.sans,
    // Escala tipográfica limitada y consistente.
    h1: { fontSize: '2.25rem', fontWeight: fontWeight.bold, lineHeight: 1.2, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.875rem', fontWeight: fontWeight.bold, lineHeight: 1.25, letterSpacing: '-0.02em' },
    h3: { fontSize: '1.5rem', fontWeight: fontWeight.semibold, lineHeight: 1.3, letterSpacing: '-0.01em' },
    h4: { fontSize: '1.375rem', fontWeight: fontWeight.semibold, lineHeight: 1.35, letterSpacing: '-0.01em' },
    h5: { fontSize: '1.125rem', fontWeight: fontWeight.semibold, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: fontWeight.semibold, lineHeight: 1.45 },
    subtitle1: { fontSize: '1rem', fontWeight: fontWeight.medium, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.875rem', fontWeight: fontWeight.semibold, lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5, color: neutral[600] },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: fontWeight.semibold,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      lineHeight: 1.6,
    },
    button: { fontWeight: fontWeight.semibold, textTransform: 'none', letterSpacing: 0 },
  },

  components: {
    // -----------------------------------------------------------------------
    // Base
    // -----------------------------------------------------------------------
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitTextSizeAdjust: '100%',
        },
        body: {
          // Ninguna página debe desbordar horizontalmente.
          overflowX: 'hidden',
        },
        // Contenido ancho (imágenes, media) nunca rompe el layout.
        'img, video, canvas, svg': {
          maxWidth: '100%',
        },
        // El foco de teclado siempre visible, en cualquier elemento.
        ':focus-visible': focusRing(),
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },

    // -----------------------------------------------------------------------
    // Superficies
    // -----------------------------------------------------------------------
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: radius.md,
        },
        // `elevation={0}` en superficies de contenido: la jerarquía la marca el borde.
        elevation0: {
          border: `1px solid ${neutral[200]}`,
        },
        elevation1: { boxShadow: shadow.sm },
        elevation2: { boxShadow: shadow.md },
        elevation3: { boxShadow: shadow.md },
        elevation8: { boxShadow: shadow.lg },
        elevation10: { boxShadow: shadow.lg },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          border: `1px solid ${neutral[200]}`,
          boxShadow: shadow.xs,
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': { paddingBottom: 20 },
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: neutral[0],
          color: neutral[900],
          borderBottom: `1px solid ${neutral[200]}`,
          // Respeta el notch en iOS cuando la barra está fija arriba.
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        },
      },
    },

    // -----------------------------------------------------------------------
    // Acciones — todas con área táctil >= 44px en dispositivos táctiles
    // -----------------------------------------------------------------------
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          minHeight: touchTarget,
          paddingInline: 20,
          '&.Mui-focusVisible': focusRing(),
        },
        sizeSmall: {
          minHeight: 36,
          paddingInline: 12,
          fontSize: '0.875rem',
          [COARSE_POINTER]: { minHeight: touchTarget },
        },
        sizeLarge: { minHeight: 52, fontSize: '1rem' },
        containedPrimary: {
          '&:hover': { backgroundColor: brand[800] },
        },
        outlined: {
          borderColor: neutral[300],
          '&:hover': { borderColor: neutral[400], backgroundColor: neutral[50] },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          '&.Mui-focusVisible': focusRing(),
          // En ratón se mantiene la densidad; con el dedo se garantizan 44px.
          [COARSE_POINTER]: {
            minWidth: touchTarget,
            minHeight: touchTarget,
          },
        },
        sizeSmall: {
          padding: 8,
          [COARSE_POINTER]: { padding: 10 },
        },
      },
    },

    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: shadow.lg,
          '&.Mui-focusVisible': focusRing(),
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: fontWeight.medium,
          borderColor: neutral[300],
          minHeight: 40,
          [COARSE_POINTER]: { minHeight: touchTarget },
          '&.Mui-selected': {
            backgroundColor: alpha(brand[700], 0.1),
            color: brand[800],
            '&:hover': { backgroundColor: alpha(brand[700], 0.16) },
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: fontWeight.medium,
          minHeight: touchTarget + 4,
          fontSize: '0.9375rem',
        },
      },
    },

    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: { '&.Mui-focusVisible': focusRing() },
      },
    },

    // -----------------------------------------------------------------------
    // Formularios — 16px siempre, para que iOS no haga zoom al enfocar
    // -----------------------------------------------------------------------
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },

    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: inputFontSize },
        input: {
          fontSize: inputFontSize,
          '&::placeholder': { color: neutral[400], opacity: 1 },
        },
        // `size="small"` reduce el alto, nunca el tamaño de letra.
        inputSizeSmall: { fontSize: inputFontSize },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          backgroundColor: neutral[0],
          '& .MuiOutlinedInput-notchedOutline': { borderColor: neutral[300] },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: neutral[400] },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            borderColor: brand[700],
          },
          '&.Mui-disabled': { backgroundColor: neutral[100] },
        },
        input: {
          minHeight: 24,
          [COARSE_POINTER]: { paddingBlock: 14 },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: { fontSize: inputFontSize },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: inputFontSize,
          color: neutral[600],
          '&.Mui-focused': { color: brand[700] },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: { marginInline: 2, fontSize: '0.8125rem' },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: { minHeight: touchTarget },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: { '&.Mui-focusVisible': focusRing() },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: { '&.Mui-focusVisible': focusRing() },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: inputFontSize,
          minHeight: touchTarget,
        },
      },
    },

    // -----------------------------------------------------------------------
    // Navegación
    // -----------------------------------------------------------------------
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          minHeight: 48,
          '&.Mui-selected': {
            backgroundColor: alpha(brand[700], 0.1),
            color: brand[800],
            '&:hover': { backgroundColor: alpha(brand[700], 0.14) },
            '& .MuiListItemIcon-root': { color: brand[700] },
            '& .MuiListItemText-primary': { fontWeight: fontWeight.semibold },
          },
          '&.Mui-focusVisible': focusRing(),
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: 40, color: neutral[500] },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${neutral[200]}`,
          backgroundImage: 'none',
        },
      },
    },

    // -----------------------------------------------------------------------
    // Datos
    // -----------------------------------------------------------------------
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderBottomColor: neutral[200],
          [t.breakpoints.down('sm')]: { padding: '10px 12px' },
        }),
        head: {
          backgroundColor: neutral[50],
          color: neutral[700],
          fontWeight: fontWeight.semibold,
          fontSize: '0.8125rem',
          letterSpacing: '0.02em',
          whiteSpace: 'nowrap',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': { borderBottom: 0 },
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        // El contenido ancho hace scroll dentro de su contenedor, no en la página.
        root: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
      },
    },

    MuiTablePagination: {
      styleOverrides: {
        toolbar: ({ theme: t }) => ({
          [t.breakpoints.down('sm')]: {
            paddingLeft: 8,
            flexWrap: 'wrap',
            justifyContent: 'center',
            rowGap: 4,
          },
        }),
        selectLabel: { marginBottom: 0 },
        displayedRows: { marginBottom: 0 },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: fontWeight.medium, borderRadius: radius.sm },
        sizeSmall: { height: 24 },
        outlined: { borderColor: neutral[300] },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: neutral[200] },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.sm, alignItems: 'center' },
        standardError: { backgroundColor: semantic.error.surface, color: semantic.error.dark },
        standardSuccess: { backgroundColor: semantic.success.surface, color: semantic.success.dark },
        standardWarning: { backgroundColor: semantic.warning.surface, color: semantic.warning.dark },
        standardInfo: { backgroundColor: semantic.info.surface, color: semantic.info.dark },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral[800],
          fontSize: '0.8125rem',
          borderRadius: radius.sm,
          padding: '6px 10px',
        },
      },
    },

    // -----------------------------------------------------------------------
    // Diálogos — casi pantalla completa en móvil, respetando safe areas
    // -----------------------------------------------------------------------
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme: t }) => ({
          borderRadius: radius.lg,
          [t.breakpoints.down('sm')]: {
            margin: 8,
            marginBottom: `calc(8px + env(safe-area-inset-bottom))`,
            width: 'calc(100% - 16px)',
            maxWidth: 'calc(100% - 16px)',
            maxHeight: 'calc(100% - 16px - env(safe-area-inset-bottom))',
            borderRadius: radius.md,
          },
        }),
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1.125rem', fontWeight: fontWeight.semibold },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          padding: 16,
          gap: 8,
          // En móvil los botones se apilan a ancho completo: más fáciles de acertar.
          [t.breakpoints.down('sm')]: {
            flexDirection: 'column-reverse',
            alignItems: 'stretch',
            '& > :not(:first-of-type)': { marginLeft: 0 },
          },
        }),
      },
    },

    MuiBottomNavigation: {
      styleOverrides: {
        root: { height: 'auto' },
      },
    },

    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minWidth: 0,
          minHeight: touchTarget,
          paddingTop: 8,
          paddingBottom: 8,
          color: neutral[500],
          '&.Mui-selected': { color: brand[700] },
        },
        label: {
          fontSize: '0.6875rem',
          '&.Mui-selected': { fontSize: '0.6875rem', fontWeight: fontWeight.semibold },
        },
      },
    },
  },
})

// Ajusta automáticamente los títulos por breakpoint (mobile-first).
theme = responsiveFontSizes(theme, { factor: 2.5 })

export default theme
export * from './tokens'
